// ──────────────────────────────────────────────────────────────────────────────
// THE BRAND CLOCK, SPA side (02 s177; 04 #136 overlay B). Every calendar DATE the SPA derives from
// "now" (a form prefill, an as-of, a month default, a due-day countdown) is taken in the timezone of
// the brand that OWNS the record (`businesses.timezone`); with no owning brand, the portal brand's;
// absent = America/Los_Angeles. Instants stay UTC ISO timestamps. This is the ONE SPA helper and it
// mirrors api/src/lib/timezones.ts#brandLocalDate (same Intl.formatToParts form, same fallback):
// `toISOString().slice(0, 10)` or `getUTCFullYear()` for "today" is banned in source.
// ──────────────────────────────────────────────────────────────────────────────

const DEFAULT_BRAND_TIMEZONE = 'America/Los_Angeles';

/** The brand-local calendar date (YYYY-MM-DD) of an instant (default: now). A missing timezone
 *  reads as the platform default; an invalid one falls back to it. */
export function brandLocalDate(timezone?: string | null, now: Date = new Date()): string {
  const fmt = (zone: string): string => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (t: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
  };
  try {
    return fmt(timezone || DEFAULT_BRAND_TIMEZONE);
  } catch {
    return fmt(DEFAULT_BRAND_TIMEZONE);
  }
}

/** Re-default a date (YYYY-MM-DD) or month (YYYY-MM) PREFILL when its owning brand changes or first
 *  becomes known: when `current` still reads the previous brand's today (or this month), it is the
 *  untouched prefill and becomes the new brand's; any other value is the admin's and is kept. Only
 *  for a NEW record's prefill, never an edit form's stored date (a stored date is never rewritten). */
export function rebaseDatePrefill(
  current: string,
  fromTimezone: string | null | undefined,
  toTimezone: string | null | undefined,
  now: Date = new Date(),
): string {
  const n = current.length === 7 ? 7 : 10;
  return current === brandLocalDate(fromTimezone, now).slice(0, n) ? brandLocalDate(toTimezone, now).slice(0, n) : current;
}
